import { HttpMethods, RequestPerformer } from '@/api/api-requests-factory';
import { Ref, ref } from 'vue';
import GetGeoPaymentMethodListRequest
  from '@/api/ps-management/geo-payment-methods/get-geo-payment-method-list.request';
import UpdateListSortGeoPaymentMethodRequest
  from '@/api/ps-management/geo-payment-methods/update-sort-geo-payment-method-list.request';
import PartialUpdateGeoSinglePaymentMethodRequest
  from '@/api/ps-management/geo-payment-methods/partial-update-geo-payment-method.request';
import GetGlobalPaymentMethodListRequest
  from '@/api/ps-management/global-payment-methods/get-global-payment-method-list.request';
import CreateGlobalPaymentMethodRequest
  from '@/api/ps-management/global-payment-methods/create-global-payment-method.request';
import DeleteGlobalPaymentMethodRequest
  from '@/api/ps-management/global-payment-methods/delete-global-payment-method.request';
import GetGlobalPaymentMethodRequest
  from '@/api/ps-management/global-payment-methods/get-global-payment-method.request';
import UpdateGlobalPaymentMethodRequest
  from '@/api/ps-management/global-payment-methods/update-global-payment-method.request';
import PartialUpdateGlobalPaymentMethodRequest
  from '@/api/ps-management/global-payment-methods/partial-update-global-payment-method.request';
import GetPaymentMethodCountryListRequest
  from '@/api/ps-management/country-list/get-payment-method-country-list.request';
import AddPaymentMethodCountriesRequest from '@/api/ps-management/country-list/add-payment-methos-countries.request';
import UpdateGeoPaymentMethodRequest from '@/api/ps-management/geo-payment-methods/update-geo-payment-method.request';
import GetGeoPaymentMethodDepositLimitsRequest
  from '@/api/ps-management/payment-method-limits/get-geo-deposit-limits.request';
import DeleteGeoPaymentMethodRequest from '@/api/ps-management/geo-payment-methods/delete-geo-payment-method.request';
import GetGeoPaymentMethodRequest from '@/api/ps-management/geo-payment-methods/get-geo-payment-method.request';
import UpdateGeoPaymentMethodDepositLimitsRequest
  from '@/api/ps-management/payment-method-limits/update-geo-deposit-limits.request';
import GetBillingProviderListRequest from '@/api/ps-management/get-billing-provider-list.request';
import CreatePaymentMethodCredentialsRequest
  from '@/api/ps-management/global-payment-methods/create-payment-method-credentials.request';
import CreateGeoSingleMethodRequest
  from '@/api/ps-management/geo-payment-methods/create-geo-single-payment-method.request';
import GetGeoMultiMethodRequest
  from '@/api/ps-management/geo-payment-methods/geo-multi-methods/get-geo-multi-method.request';
import GetAvailableAssociablePaymentMethodListRequest
  from '@/api/ps-management/geo-payment-methods/geo-multi-methods/get-available-associable-billing-method-list.request';
import UpdateAssociatedPaymentMethodRequest
  from '@/api/ps-management/geo-payment-methods/geo-multi-methods/update-associated-billing-method.request';
import AssociateBillingMethodListRequest
  from '@/api/ps-management/geo-payment-methods/geo-multi-methods/associate-billing-method.request';
import DisassociateBillingMethodRequest
  from '@/api/ps-management/geo-payment-methods/geo-multi-methods/disassociate-billing-method.request';
import GetAssociatedPaymentMethodListRequest
  from '@/api/ps-management/geo-payment-methods/geo-multi-methods/get-associated-billing-method-list.request';
import CreateGeoMultiMethodRequest
  from '@/api/ps-management/geo-payment-methods/geo-multi-methods/create-geo-multi-method.request';
import UpdateGeoMultiMethodRequest
  from '@/api/ps-management/geo-payment-methods/geo-multi-methods/update-geo-multi-method.request';
import CreatePaymentMethodExceptionLimitRequest
  from '@/api/ps-management/payment-method-exception-limit/create-exception-limit.request';
import UpdatePaymentMethodExceptionLimitRequest
  from '@/api/ps-management/payment-method-exception-limit/update-exception-limit.request';
import DeletePaymentMethodExceptionLimitRequest
  from '@/api/ps-management/payment-method-exception-limit/delete-exception-limit.request';
import GetPaymentMethodExceptionLimitListRequest
  from '@/api/ps-management/payment-method-exception-limit/get-exception-limit-list.request';
import GetPaymentMethodGeneralLimitListRequest
  from '@/api/ps-management/payment-method-exception-limit/get-general-limit-list.request';
import GetConvertableCurrencyListRequest
  from '@/api/ps-management/global-currency-converter/get-convertable-currency-list.request';
import GetPaymentMethodConverterListRequest
  from '@/api/ps-management/global-currency-converter/get-payment-method-currency-converter-list.request';
import UpdatePaymentMethodCurrencyConverterRequest
  from '@/api/ps-management/global-currency-converter/update-payment-method-currency-converter.request';
import GetGeoPaymentMethodAvailabilityHoursRequest
  from '@/api/ps-management/geo-payment-method-availability-hours/get-geo-payment-method-availability-hours.request';
import UpdateGeoPaymentMethodAvailabilityHoursRequest
  from '@/api/ps-management/geo-payment-method-availability-hours/update-geo-payment-method-availability-hours.request';

interface PsManagementApiResourcesParams {
  requestPerformer: RequestPerformer;
  apiUrl: Ref<string>;
  multibrandApiHost: Ref<string>;
  wlSlug: Ref<string>;
}

export class PsManagementApiResources {
  private readonly requestPerformer: RequestPerformer;
  private readonly apiUrl: Ref<string>;
  private readonly billingProviderApiUrl: Ref<string>;
  private readonly multibrandApiUrl: Ref<string>;

  public constructor(
    {
      requestPerformer,
      apiUrl,
      multibrandApiHost,
      wlSlug,
    }: PsManagementApiResourcesParams,
  ) {
    this.requestPerformer = requestPerformer;
    this.apiUrl = apiUrl;

    this.billingProviderApiUrl = ref(`${apiUrl.value}/billingProvider`);
    this.multibrandApiUrl = ref(
      `${multibrandApiHost.value}/api/v1.0/front/proxy/${wlSlug.value}/billingProvider`,
    );
  }

  public get getBillingProviderListRequest(): GetBillingProviderListRequest {
    return new GetBillingProviderListRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}`,
      HttpMethods.GET,
    );
  }

  // ====================================
  // === GEO PAYMENT METHODS REQUESTS ===
  // ====================================

  public get createPaymentMethodCredentialsRequest(): CreatePaymentMethodCredentialsRequest {
    return new CreatePaymentMethodCredentialsRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethodCredentials`,
      HttpMethods.POST,
    );
  }

  public get getGeoPaymentMethodListRequest(): GetGeoPaymentMethodListRequest {
    return new GetGeoPaymentMethodListRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethodByCountry`,
      HttpMethods.GET,
    );
  }

  public get createGeoSingleMethodRequest(): CreateGeoSingleMethodRequest {
    return new CreateGeoSingleMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethodByCountry`,
      HttpMethods.POST,
    );
  }

  public get updateListSortGeoPaymentMethodRequest(): UpdateListSortGeoPaymentMethodRequest {
    return new UpdateListSortGeoPaymentMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethodByCountry`,
      HttpMethods.PUT,
    );
  }

  public get updateGeoPaymentMethodRequest(): UpdateGeoPaymentMethodRequest {
    return new UpdateGeoPaymentMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethodSettings`,
      HttpMethods.PATCH,
    );
  }

  public get partialUpdateGeoSinglePaymentMethodRequest(): PartialUpdateGeoSinglePaymentMethodRequest {
    return new PartialUpdateGeoSinglePaymentMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethodSettings`,
      HttpMethods.PATCH,
    );
  }

  public get deleteGeoPaymentMethodRequest(): DeleteGeoPaymentMethodRequest {
    return new DeleteGeoPaymentMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethodSettings`,
      HttpMethods.DELETE,
    );
  }

  public get getGeoPaymentMethodRequest(): GetGeoPaymentMethodRequest {
    return new GetGeoPaymentMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethodSettings`,
      HttpMethods.GET,
    );
  }

  public get getGeoPaymentMethodAvailabilityHoursRequest(): GetGeoPaymentMethodAvailabilityHoursRequest {
    return new GetGeoPaymentMethodAvailabilityHoursRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethodSettings`,
      HttpMethods.GET,
    );
  }

  public get updateGeoPaymentMethodAvailabilityHoursRequest(): UpdateGeoPaymentMethodAvailabilityHoursRequest {
    return new UpdateGeoPaymentMethodAvailabilityHoursRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethodSettings`,
      HttpMethods.PATCH,
    );
  }

  // ==========================================
  // === GEO MULTI PAYMENT METHODS REQUESTS ===
  // ==========================================

  public get getGeoMultiMethodRequest(): GetGeoMultiMethodRequest {
    return new GetGeoMultiMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMultiMethod`,
      HttpMethods.GET,
    );
  }

  public get createGeoMultiMethodRequest(): CreateGeoMultiMethodRequest {
    return new CreateGeoMultiMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMultiMethod`,
      HttpMethods.POST,
    );
  }

  public get updateGeoMultiMethodRequest(): UpdateGeoMultiMethodRequest {
    return new UpdateGeoMultiMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMultiMethod`,
      HttpMethods.PUT,
    );
  }

  public get deleteGeoMultiMethodRequest(): DeleteGeoPaymentMethodRequest {
    return new DeleteGeoPaymentMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMultiMethod`,
      HttpMethods.DELETE,
    );
  }

  public get getAvailableAssociablePaymentMethodIListRequest(): GetAvailableAssociablePaymentMethodListRequest {
    return new GetAvailableAssociablePaymentMethodListRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMultiMethod`,
      HttpMethods.GET,
    );
  }

  public get associateBillingMethodListRequest(): AssociateBillingMethodListRequest {
    return new AssociateBillingMethodListRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMultiMethod`,
      HttpMethods.POST,
    );
  }

  public get disassociateBillingMethodRequest(): DisassociateBillingMethodRequest {
    return new DisassociateBillingMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMultiMethod`,
      HttpMethods.DELETE,
    );
  }

  public get getAssociatedPaymentMethodListRequest(): GetAssociatedPaymentMethodListRequest {
    return new GetAssociatedPaymentMethodListRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMultiMethod`,
      HttpMethods.GET,
    );
  }

  public get updateAssociatedPaymentMethodRequest(): UpdateAssociatedPaymentMethodRequest {
    return new UpdateAssociatedPaymentMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMultiMethod`,
      HttpMethods.PUT,
    );
  }

  // =======================================
  // === GLOBAL PAYMENT METHODS REQUESTS ===
  // =======================================

  public get getGlobalPaymentMethodListRequest(): GetGlobalPaymentMethodListRequest {
    return new GetGlobalPaymentMethodListRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethod`,
      HttpMethods.GET,
    );
  }

  public get createGlobalPaymentMethodRequest(): CreateGlobalPaymentMethodRequest {
    return new CreateGlobalPaymentMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethod`,
      HttpMethods.POST,
    );
  }

  public get deleteGlobalPaymentMethodRequest(): DeleteGlobalPaymentMethodRequest {
    return new DeleteGlobalPaymentMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethod`,
      HttpMethods.DELETE,
    );
  }

  public get getGlobalPaymentMethodRequest(): GetGlobalPaymentMethodRequest {
    return new GetGlobalPaymentMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethod`,
      HttpMethods.GET,
    );
  }

  public get updateGlobalPaymentMethodRequest(): UpdateGlobalPaymentMethodRequest {
    return new UpdateGlobalPaymentMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethod`,
      HttpMethods.PUT,
    );
  }

  public get partialUpdateGlobalPaymentMethodRequest(): PartialUpdateGlobalPaymentMethodRequest {
    return new PartialUpdateGlobalPaymentMethodRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethod`,
      HttpMethods.PATCH,
    );
  }

  // ======================================
  // === PAYMENT SYSTEMS COUNTRIES LIST ===
  // ======================================

  public get getPaymentMethodCountryListRequest(): GetPaymentMethodCountryListRequest {
    return new GetPaymentMethodCountryListRequest(
      this.requestPerformer,
      `${this.apiUrl.value}/paymentMethodGeo`,
      HttpMethods.GET,
    );
  }

  public get addPaymentMethodCountriesRequest(): AddPaymentMethodCountriesRequest {
    return new AddPaymentMethodCountriesRequest(
      this.requestPerformer,
      `${this.apiUrl.value}/paymentMethodGeo`,
      HttpMethods.POST,
    );
  }

  // ==============================
  // === PAYMENT SYSTEMS LIMITS ===
  // ==============================
  public get getGeoPaymentMethodDepositLimitsRequest(): GetGeoPaymentMethodDepositLimitsRequest {
    return new GetGeoPaymentMethodDepositLimitsRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/billingPaymentMethodDepositLimit`,
      HttpMethods.GET,
    );
  }

  public get updateGeoPaymentMethodDepositLimitsRequest(): UpdateGeoPaymentMethodDepositLimitsRequest {
    return new UpdateGeoPaymentMethodDepositLimitsRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/billingPaymentMethodDepositLimit`,
      HttpMethods.PATCH,
    );
  }

  // ==============================
  // === PAYMENT SYSTEM EXCEPTIONS LIMIT ===
  // ==============================

  public get getPaymentMethodExceptionLimitListRequest(): GetPaymentMethodExceptionLimitListRequest {
    return new GetPaymentMethodExceptionLimitListRequest(
      this.requestPerformer,
      this.multibrandApiUrl.value,
      HttpMethods.GET,
    );
  }

  public get getPaymentMethodGeneralLimitListRequest(): GetPaymentMethodGeneralLimitListRequest {
    return new GetPaymentMethodGeneralLimitListRequest(
      this.requestPerformer,
      this.billingProviderApiUrl.value,
      HttpMethods.GET,
    );
  }

  public get updatePaymentMethodExceptionLimitRequest(): UpdatePaymentMethodExceptionLimitRequest {
    return new UpdatePaymentMethodExceptionLimitRequest(
      this.requestPerformer,
      this.multibrandApiUrl.value,
      HttpMethods.PATCH,
    );
  }

  public get deletePaymentMethodExceptionLimitRequest(): DeletePaymentMethodExceptionLimitRequest {
    return new DeletePaymentMethodExceptionLimitRequest(
      this.requestPerformer,
      this.multibrandApiUrl.value,
      HttpMethods.DELETE,
    );
  }

  public get addPaymentMethodExceptionLimitRequest(): CreatePaymentMethodExceptionLimitRequest {
    return new CreatePaymentMethodExceptionLimitRequest(
      this.requestPerformer,
      this.multibrandApiUrl.value,
      HttpMethods.POST,
    );
  }

  // ==============================
  // === PAYMENT SYSTEM CURRENCY CONVERTER ===
  // ==============================

  public get getConvertableCurrencyListRequest(): GetConvertableCurrencyListRequest {
    return new GetConvertableCurrencyListRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/convertableCurrencyList`,
      HttpMethods.GET,
    );
  }

  public get getPaymentMethodConverterListRequest(): GetPaymentMethodConverterListRequest {
    return new GetPaymentMethodConverterListRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethod`,
      HttpMethods.GET,
    );
  }

  public get updatePaymentMethodCurrencyConverterRequest(): UpdatePaymentMethodCurrencyConverterRequest {
    return new UpdatePaymentMethodCurrencyConverterRequest(
      this.requestPerformer,
      `${this.billingProviderApiUrl.value}/paymentMethod`,
      HttpMethods.PUT,
    );
  }
}
